import * as PIXI from 'pixi.js'
import orderBy from "lodash/orderBy";

/**
 * @typedef {Object} Config
 * @prop {[String]} assets - Assets URLs array
 *
 * @typedef {Object<string, PIXI.loaders.Resource>} ResourcesCache
 */

export default class Loader {

    /**
     * @type {PIXI.loaders.Loader}
     */
    _pixiLoader = new PIXI.loaders.Loader();

    /**
     * @type {[String]}
     */
    _assets = [];

    /**
     * PIXI Loader wrapper. This Loader is meant to be more Vue friendly, it can
     * load several times the same assets without the PIXI TextureCache and
     * BaseTextureCache warnings. Note that it uses its own cache.
     * @param {Config} config
     */
    constructor(config) {
        this._assets = config && config.assets ? [...config.assets] : [];
    }

    get progress() {
        return this._pixiLoader.progress
    }

    get loading() {
        return this._pixiLoader.loading
    }

    get loaded() {
        return this.progress === 100
    }

    get pixiLoader() {
        return this._pixiLoader
    }

    /**
     * Resources loaded. Sometimes `loaderInstance.pixiLoader.resources` might
     * be empty, so, always use `loaderInstance.resources`.
     * @type {ResourcesCache}
     */
    get resources() {
        const {_assets} = this
        return _assets
            .filter(asset => !!Loader._ResourcesCache[asset])
            .reduce((prev, current) => {
                prev[current] = Loader._ResourcesCache[current]
                return prev
            }, {})
    }

    /**
     * Resources loaded. Sometimes `loaderInstance.pixiLoader.resources` might
     * be empty, so, always use `loaderInstance.resources`.
     * @type {ResourcesCache}
     */
    get animations() {
        return Loader._Animations;
    }

    /**
     * Filters assets that aren't loaded. In other words, the assets missing in
     * `Loader._ResourcesCache`.
     * @returns {[String]}
     * @private
     */
    _filterAssets() {
        const {_assets} = this
        return _assets.filter(asset => !Loader._urls[asset])
    }

    /**
     * Adds the resources of this instance `_pixiLoader` to
     * `Loader._ResourcesCache` if they aren't already there.
     * @private
     */
    _updateResources() {
        const {_pixiLoader} = this;
        for (let key in _pixiLoader.resources) {
            const resource = Loader._ResourcesCache[key]
            if (!resource) {
                Loader._ResourcesCache[key] = _pixiLoader.resources[key]
            }
        }
    }

    _updateUrls(assets) {
        assets.forEach(asset => {
            Loader._urls.push(asset)
        })
    }

    /**
     * Loads assets passed in config.
     * @return {Promise<Loader>}
     */
    load() {
        const {loading, loaded, _pixiLoader} = this
        if (loading || loaded) {
            return Promise.resolve(this)
        }
        return new Promise((resolve, reject) => {
            const assets = this._filterAssets()
            assets.forEach(asset => {
                _pixiLoader.add(asset)
            })
            this._updateUrls(assets)
            _pixiLoader.onLoad.add((l, resource) => {
                if (resource.spritesheet) {
                    for (let [key, animation] of Object.entries(resource.spritesheet.animations)) {
                        this._addAnimation(key, animation);
                    }
                }
            });
            _pixiLoader.onComplete.add(() => {
                for (let [key, animations] of Object.entries(Loader._Animations)) {
                    Loader._Animations[key] = orderBy(animations, ["textureCacheIds"])
                }
                this._updateResources();
                resolve(this)
            })
            _pixiLoader.onError.add(reject);
            _pixiLoader.load()
        })
    }

    /**
     * Loads assets passed in config.
     * @return {Promise<Loader>}
     */
    loadAsset(assetIndex) {
        const {loading, loaded, _pixiLoader} = this;
        if (loading || loaded) {
            return Promise.resolve(this)
        }
        return new Promise((resolve, reject) => {
            const assets = this._filterAssets()
            const asset = assets[assetIndex];
            if (!_pixiLoader.resources[asset]) {
                _pixiLoader.add(asset)
            }

            this._updateUrls(assets)

            _pixiLoader.onLoad.add((l, resource) => {
                if (resource.spritesheet) {
                    for (let [key, animation] of Object.entries(resource.spritesheet.animations)) {
                        this._addAnimation(key, animation);
                    }
                }
            });

            _pixiLoader.onComplete.add(() => {
                for (let [key, animations] of Object.entries(Loader._Animations)) {
                    Loader._Animations[key] = orderBy(animations, ["textureCacheIds"])
                }
                this._updateResources();
                resolve(this)
            })

            _pixiLoader.onError.add(reject);
            _pixiLoader.load()
        })
    }

    reset() {
        this._pixiLoader.reset()
    }

    addImage(url) {
        this._assets.push(url)
    }

    _addAnimation(key, anim) {
        if (!Loader._Animations.hasOwnProperty(key)) {
            Loader._Animations[key] = [];
        }
        Loader._Animations[key] = Loader._Animations[key].concat(anim);
    }

    /**
     * All resources loaded with instances of this Loader are saved in this
     * object.
     * @type {ResourcesCache}
     * @private
     */
    static _ResourcesCache = {};

    static _urls = []

    static _Animations = {};
}



// WEBPACK FOOTER //
// ./src/app/utils/pixi/Loader.js